import { useEffect, useState } from "react";
import { Col, Row, Label, Form, FormGroup } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";

import typography from "../typography";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import ModalSuccessImage from "../../../assets/images/app/common/aou-modal-success-img.svg";

import { formatCombinedDateTime } from "../../../utils/dateFormatUtils";
import calendarIcon from "../../../assets/images/app/common/calendar-icon.svg";
import clockIcon from "../../../assets/images/app/common/clock-icon.svg";
import CustomDatePicker from "../../../components/DatePicker/";
import { TimePicker } from "../../../components/TimePicker";
import { createScheduledNotification } from "../../../api/api.service";
import moment from "moment";
import { validationSchema } from "../validation";
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText";

export default function AddNotification() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT);
    };

    const formik = useFormik({
        initialValues: {
            title: "",
            message: "",
            scheduleDate: "",
            scheduleTime: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            await handleSaveClick(values);
        },
    });

    useEffect(() => {
        setLoading(true);
        const currentDate = moment.utc().format("MMMM DD, YYYY");
        const currentTime = moment.utc().format("h:mm A");

        formik.setValues({
            title: "",
            message: "",
            scheduleDate: currentDate,
            scheduleTime: currentTime,
        });
        setLoading(false);
    }, [id]);

    const handleSaveClick = async (values) => {
        try {
            setIsSaving(true);
            const scheduleAt = formatCombinedDateTime(
                values.scheduleDate,
                values.scheduleTime
            );

            const requestBody = {
                title: values.title.trim(),
                message: values.message.trim(),
                scheduleAt: scheduleAt,
            };

            await createScheduledNotification(requestBody);

            setIsModalActive(true);
        } catch (error) {
            console.error("Error creating notification:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleModalYes = () => {
        setIsModalActive(false);
        history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT);
    };

    return (
        <>
            {loading ? (
                <LoaderFallbackUI isLoader={loading} />
            ) : (
                <>
                    <div className="edit-notification-heading">
                        <HeadingWithBackArrow
                            backArrowClickHandler={backButtonHandler}
                            headingTitle={typography.ADD_NOTIFICATION}
                            showToggle={false}
                        />
                        <div className="edit-notification-heading-button">
                            <CustomButton
                                title={isSaving ? "Saving..." : "Save"}
                                className="edit-notification-update-button"
                                onClick={formik.handleSubmit}
                                type="submit"
                            />
                        </div>
                    </div>

                    <Form
                        onSubmit={formik.handleSubmit}
                        className="edit-notification-form"
                    >
                        <FormGroup>
                            <Label
                                className="edit-notification-form-label"
                                for="notification-title"
                            >
                                {typography.TITLE}
                            </Label>
                            <TextInput
                                id="notification-title"
                                name="title"
                                placeholder="Enter notification title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="edit-notification-form-input hide-default-icon"
                                required
                            />
                            <FormikErrorText
                                formikInstance={formik}
                                fieldName="title"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label
                                className="edit-notification-form-label"
                                for="notification-message"
                            >
                                {typography.NOTIFICATION_MESSAGE}
                            </Label>
                            <textarea
                                id="notification-message"
                                name="message"
                                className="edit-notification-form-textarea"
                                placeholder="Enter notification message"
                                value={formik.values.message}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                rows={6}
                                required
                            />
                            <FormikErrorText
                                formikInstance={formik}
                                fieldName="message"
                            />
                        </FormGroup>

                        <FormGroup className="edit-notification-form-section">
                            <Row>
                                <Col md="6">
                                    <Label
                                        className="edit-notification-form-label"
                                        for="schedule-date"
                                    >
                                        {typography.SCHEDULE_DATE}{" "}
                                        <span className="text-muted">
                                            (UTC)
                                        </span>
                                    </Label>
                                    <div className="edit-notification-input-with-icon">
                                        <CustomDatePicker
                                            id="schedule-date"
                                            name="scheduleDate"
                                            placeholder="Select date"
                                            value={formik.values.scheduleDate}
                                            onChange={(val) =>
                                                formik.setFieldValue(
                                                    "scheduleDate",
                                                    val
                                                )
                                            }
                                            onBlur={() =>
                                                formik.setFieldTouched(
                                                    "scheduleDate",
                                                    true
                                                )
                                            }
                                            className="edit-notification-form-input hide-default-icon"
                                        />
                                        <img
                                            src={calendarIcon}
                                            alt="calendar"
                                            className="input-icon"
                                        />
                                    </div>
                                    <FormikErrorText
                                        formikInstance={formik}
                                        fieldName="scheduleDate"
                                    />
                                </Col>
                                <Col md="6">
                                    <Label
                                        className="edit-notification-form-label"
                                        for="schedule-time"
                                    >
                                        {typography.SCHEDULE_TIME}{" "}
                                        <span className="text-muted">
                                            (UTC)
                                        </span>
                                    </Label>
                                    <div className="edit-notification-input-with-icon">
                                        <TimePicker
                                            id="schedule-time"
                                            name="scheduleTime"
                                            placeholder="Select time"
                                            value={formik.values.scheduleTime}
                                            onChange={(val) =>
                                                formik.setFieldValue(
                                                    "scheduleTime",
                                                    val
                                                )
                                            }
                                            onBlur={() =>
                                                formik.setFieldTouched(
                                                    "scheduleTime",
                                                    true
                                                )
                                            }
                                            className="edit-notification-form-input hide-default-icon"
                                            required
                                        />
                                        <img
                                            src={clockIcon}
                                            alt="clock"
                                            className="input-icon"
                                        />
                                    </div>
                                    <FormikErrorText
                                        formikInstance={formik}
                                        fieldName="scheduleTime"
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Form>

                    <ConfirmationModal
                        isOpen={isModalActive}
                        modalType="SAVE"
                        onYes={handleModalYes}
                        size="md"
                        showNoButton={false}
                        customImageSrc={ModalSuccessImage}
                    />
                </>
            )}
        </>
    );
}
